import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Polygon,
  PolygonData,
  PolygonOverlay,
  PolygonOverlayHandle,
} from '@/components/ui/polygon/PolygonOverlay';
import { Separator } from '@/components/ui/Separator';
import {
  AuditLogInsertInput,
  Device,
  Media,
  Roi,
  RoiInsertInput,
  RoiUpdates,
  ZoneInsertInput,
} from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import { useFetchMedia } from '@/hooks/useFetchMedia';
import { AuditLogAction, AuditLogEntityType } from '@/models/audit-log.models';
import { DEFAULT_UUID } from '@/models/constants';
import { MediaKind } from '@/models/device.models';
import { MediaMimeRootType } from '@/models/media';
import { FeatureName } from '@/models/role.models';
import { ClientPolygon, RoiConfig } from '@/models/zone.models';
import { createAuditLogInput } from '@/utils/audit-log.function';
import { downloadMedia, getMediaEndpointUrl } from '@/utils/media.functions';
import hasPermission from '@/utils/permission.function';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Download,
  Edit,
  Hexagon,
  MapPinned,
  PlusIcon,
  RefreshCcw,
  X,
} from 'lucide-react';
import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { v4 as uuid } from 'uuid';
import { createUpdateRoiMutation } from '../graphql/mutation';
import { zoneQuery } from '../graphql/query';
import { MediaKindTabs } from './MediaKindTabs';
import SelectDeviceImageSlider, {
  SelectDeviceImageSliderHandle,
} from './SelectDeviceImageSlider';
import { ZonePickerDeviceDialog } from './ZonePickerDialog';

const PolygonEditor: React.FC<{
  device: Device;
  rois: Roi[] | undefined;
  onRefetchRoiZonData: () => void;
  onChildMapVisible: (visible: boolean) => void;
  onSelectedTimestampChange?: (timestamp?: string) => void;
  onSelectedMediaKindChange?: (kind: MediaKind) => void;
}> = ({
  device,
  rois,
  onChildMapVisible,
  onRefetchRoiZonData,
  onSelectedTimestampChange,
  onSelectedMediaKindChange,
}) => {
  const [zoneSelectDialogOpen, setZoneSelectDialogOpen] = useState(false);
  const [mediaKind, setMediaKind] = useState<MediaKind>(MediaKind.PROCESSED);
  const [mediaEndpointUrl, setMediaEndpointUrl] = useState<string>();
  const [fetchedImage, setFetchedImage] = useState<Media | null>();
  const [polygonData, setPolygonData] = useState<PolygonData[]>();
  const [clientPolygons, setClientPolygons] = useState<ClientPolygon[]>();
  const [viewingPolygonIndex, setViewingPolygonIndex] = useState<number>(-1);
  const [selectedZoneKey, setSelectedZoneKey] = useState<string>();

  const [isDrawingPolygon, setIsDrawingPolygon] = useState(false);
  const [drawingPolygon, setDrawingPolygon] = useState<Polygon>([]);
  const [canEditPolygon, setCanEditPolygon] = useState(false);

  const childRef = React.useRef<PolygonOverlayHandle>(null);
  const [mediaFetching, setMediaFetching] = useState<boolean>(false);
  const imageSliderRef = useRef<SelectDeviceImageSliderHandle>(null);
  const { mediaUrl, loading: imageLoading } = useFetchMedia({
    mediaEndpointUrl,
  });

  const isVideo = fetchedImage?.mimeRootType === MediaMimeRootType.VIDEO;

  const {
    register,
    reset,
    getValues,
    control,
    setValue,
    formState: { isDirty, isValid, isLoading },
  } = useForm<{
    clientPolygons: ClientPolygon[];
  }>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'clientPolygons',
  });
  const polygonsFormValue = useWatch({ control, name: 'clientPolygons' });

  // Queries, Mutations
  const { data: zoneData, refetch: refetchZone } = useQuery({
    queryKey: ['Zone'],
    queryFn: () =>
      execute({
        query: zoneQuery,
      }),
    select: (data) => data,
    retry: false,
    staleTime: 5000,
    enabled: hasPermission(FeatureName.VIEW_ROI_ZONE),
  });

  const { mutateAsync: zoneRoiMutation } = useMutation({
    mutationKey: ['CreateUpdateRoiMutation'],
    mutationFn: (options: {
      zoneInsertInputs: ZoneInsertInput[];
      roiInsertInputs: RoiInsertInput[];
      deleteRoiInputs: string[];
      updateRoiInputs: RoiUpdates[];
      auditLogInput: AuditLogInsertInput;
    }) => {
      return execute({
        query: createUpdateRoiMutation,
        variables: {
          ...options,
        },
      });
    },
    onSuccess: async () => {
      await refetchZone();

      toast.success('Polygons updated successfully!', {
        duration: 3000,
      });
      onRefetchRoiZonData();
    },
  });

  const handlePolygonsChange = (polygons: PolygonData[]) => {
    polygons.forEach((polygon, index) => {
      setValue(`clientPolygons.${index}.polygon`, polygon.polygon, {
        shouldValidate: true,
        shouldDirty: true,
      });
    });
    setIsDrawingPolygon(false);
  };

  const handleRemovePolygon = (removeIndex: number) => {
    remove(removeIndex);
  };

  const handleFormSubmit = async (event: FormEvent) => {
    event.preventDefault();

    const now = new Date().toISOString();

    const updateRois = polygonsFormValue.map((roi) => {
      const newRoi = { ...roi };

      const matchedZone = roi.zoneKey
        ? zoneData?.zone.find((zone) => zone.key === roi.zoneKey)
        : undefined;

      if (roi.zoneKey) {
        newRoi.zoneId = matchedZone?.id;
        if (!matchedZone) newRoi.newZoneId = uuid();
      }

      if (!roi.roiId) {
        newRoi.newRoiId = uuid();
      }

      return newRoi;
    });

    // Create zone inputs for new zones
    const zoneInsertInputs: ZoneInsertInput[] = updateRois
      .filter((roi) => roi.newZoneId)
      .map((roi) => ({
        id: roi.newZoneId,
        key: roi.zoneKey,
        createdAt: now,
        updatedAt: now,
      }));

    // Determine deleted ROIs
    const deleteRoiInputs =
      clientPolygons
        ?.filter(
          (originalRoi) =>
            !updateRois.some((roi) => roi.roiId === originalRoi.roiId),
        )
        .map((roi) => roi.roiId as string) ?? [];

    // New ROIs to insert
    const roiInsertInputs: RoiInsertInput[] = updateRois
      .filter((roi) => roi.newRoiId)
      .map((roi) => ({
        id: roi.newRoiId!,
        name: roi.name,
        config: { Points: roi.polygon },
        rowVersion: DEFAULT_UUID,
        createdAt: now,
        updatedAt: now,
        deviceId: device.id,
        zoneId: roi.zoneKey ? (roi.zoneId ?? roi.newZoneId) : undefined,
      }));

    // Existing ROIs to update
    const updateRoiInputs: RoiUpdates[] = updateRois
      .filter((roi) =>
        clientPolygons?.some((original) => original.roiId === roi.roiId),
      )
      .map((roi) => {
        const config: RoiConfig = { Points: roi.polygon };
        return {
          where: { id: { _eq: roi.roiId! } },
          _set: {
            config,
            rowVersion: uuid(),
            updatedAt: now,
            zoneId: roi.newZoneId ?? roi.zoneId,
            name: roi.name,
          },
        };
      });

    console.log({
      deleteRoiInputs,
      roiInsertInputs,
      updateRoiInputs,
      zoneInsertInputs,
    });
    const deleteRoiInputsEntities = deleteRoiInputs.map((id) => ({
      entity: AuditLogEntityType.ROI,
      id,
    }));
    const roiInsertInputsEntities = roiInsertInputs.map((input) => ({
      entity: AuditLogEntityType.ROI,
      id: input.id!,
    }));

    const updateRoiInputsEntities = updateRoiInputs.map((input) => ({
      entity: AuditLogEntityType.ROI,
      id: input._set?.id as string,
    }));

    const zoneInsertInputsEntities = zoneInsertInputs.map((input) => ({
      entity: AuditLogEntityType.ZONE,
      id: input.id!,
    }));

    const entities = [
      {
        entity: AuditLogEntityType.DEVICE,
        id: device.id,
      },
      ...deleteRoiInputsEntities,
      ...roiInsertInputsEntities,
      ...updateRoiInputsEntities,
      ...zoneInsertInputsEntities,
    ];

    await zoneRoiMutation({
      deleteRoiInputs,
      roiInsertInputs,
      updateRoiInputs,
      zoneInsertInputs,
      auditLogInput: createAuditLogInput({
        action: AuditLogAction.UPDATE_DEVICE_ZONE_ROI,
        entities,
      }),
    });
    setCanEditPolygon(false);
  };

  const handleToggleOpen = (newZoneKey?: string) => {
    if (newZoneKey) {
      // If use has selected a new zoneKey, update the form.
      setValue(`clientPolygons.${viewingPolygonIndex}.zoneKey`, newZoneKey, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    setZoneSelectDialogOpen(!zoneSelectDialogOpen);
    setViewingPolygonIndex(-1);
    onChildMapVisible(false);
  };

  const openZoneSelectDialog = (index: number, zoneKey: string) => {
    setViewingPolygonIndex(index);
    setSelectedZoneKey(zoneKey);
    setZoneSelectDialogOpen(true);
    onChildMapVisible(true);
  };

  const cancelChanges = (e: FormEvent) => {
    e.preventDefault();
    if (isDrawingPolygon) {
      setIsDrawingPolygon(false);
    }
    reset({ clientPolygons });
    setCanEditPolygon(false);
  };

  const handleFinishDrawing = () => {
    childRef.current?.closePolygon();
  };

  const handleUndoDrawingPolygon = () => {
    remove(polygonsFormValue.length - 1);
    setIsDrawingPolygon(false);
  };

  useEffect(() => {
    if (zoneData && rois) {
      const nextClientPolygons = (rois ?? []).map((roi) => {
        return {
          name: roi.name,
          zoneId: roi.zoneId as string,
          roiId: roi.id,
          zoneKey: zoneData?.zone.find((zone) => zone.id === roi.zoneId)
            ?.key as string,
          polygon: (roi.config as RoiConfig).Points,
        };
      });
      setClientPolygons(nextClientPolygons);
      reset({ clientPolygons: nextClientPolygons });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rois, zoneData]);

  useEffect(() => {
    if (!polygonsFormValue) {
      return;
    }
    setPolygonData(
      polygonsFormValue.map((roi) => {
        return {
          name: roi.name,
          polygon: roi.polygon,
        };
      }),
    );
  }, [polygonsFormValue]);

  useEffect(() => {
    if (fetchedImage === undefined) {
      return;
    }
    if (fetchedImage === null) {
      setMediaEndpointUrl('');
      return;
    }
    const mediaEndpointUrl = getMediaEndpointUrl({
      mediaId: fetchedImage.mediaId!,
      timestamp: fetchedImage.time,
    });
    setMediaEndpointUrl(mediaEndpointUrl);
  }, [fetchedImage]);

  const handleAddRoi = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    append({
      polygon: [],
      name: `ROI ${polygonsFormValue.length + 1}`,
      zoneKey: '',
    });
    setCanEditPolygon(true);
    setIsDrawingPolygon(true);
  };

  const handleUpdatePolygon = (index: number) => {
    setValue(`clientPolygons.${index}.polygon`, [], {
      shouldValidate: true,
      shouldDirty: true,
    });
    setIsDrawingPolygon(true);
  };

  const handleDownloadClick = async () => {
    await downloadMedia({
      device,
      media: fetchedImage,
    });
  };

  const hasPolygons = (polygonData?.length ?? 0) > 0;
  const showAddRoiButton = canEditPolygon || !hasPolygons;

  useEffect(() => {
    onSelectedMediaKindChange?.(mediaKind);
  }, [mediaKind, onSelectedMediaKindChange]);

  return (
    <>
      <Card className="text-cen relative">
        <CardHeader>
          <CardTitle className="flex flex-row justify-between">
            Device Data
          </CardTitle>
          <div className="absolute top-4 right-4 flex items-center gap-2">
            <Button
              disabled={mediaFetching}
              variant={'outline'}
              size={'icon-sm'}
              onClick={() => imageSliderRef.current?.handleRefreshClick()}
            >
              <RefreshCcw />
            </Button>
            <MediaKindTabs mediaKind={mediaKind} setMediaKind={setMediaKind} />
            <Button
              variant={'outline'}
              size={'icon-sm'}
              onClick={() => void handleDownloadClick()}
              disabled={!mediaUrl}
            >
              <Download />
            </Button>
          </div>
        </CardHeader>

        {!isLoading && (
          <>
            <form
              onSubmit={(e) => void handleFormSubmit(e)}
              className="space-y-6"
            >
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-center">
                    <PolygonOverlay
                      ref={childRef}
                      isVideo={isVideo}
                      mediaUrl={mediaUrl ?? undefined}
                      loading={imageLoading}
                      canEdit={canEditPolygon}
                      isDrawingPolygon={isDrawingPolygon}
                      onPolygonsChange={handlePolygonsChange}
                      onUpdateDrawingPolygon={setDrawingPolygon}
                      polygonData={polygonData}
                    />
                  </div>
                  <SelectDeviceImageSlider
                    ref={imageSliderRef}
                    defaultLive={true}
                    device={device}
                    mediaKind={mediaKind}
                    onImageChange={setFetchedImage}
                    setMediaKind={setMediaKind}
                    setMediaFetching={setMediaFetching}
                    onViewingTimestampChange={onSelectedTimestampChange}
                  />
                  {hasPermission(FeatureName.VIEW_ROI_ZONE) && (
                    <>
                      <Separator />

                      {fields.map((_field, index) => (
                        <div className="grid grid-cols-12 gap-2" key={index}>
                          <div className="col-span-4 flex">
                            <Input
                              type="text"
                              className="hidden"
                              placeholder="ROI name"
                              {...register(`clientPolygons.${index}.polygon`, {
                                required: true,
                                validate: (val: number[][]) => {
                                  if (val && val.length > 0) {
                                    return true;
                                  } else {
                                    return false;
                                  }
                                },
                              })}
                            />
                            <div className="grid flex-1 gap-2">
                              {index === 0 && (
                                <Label htmlFor={`name-${index}`}>Name</Label>
                              )}
                              <Input
                                type="text"
                                placeholder="ROI name"
                                readOnly={
                                  !hasPermission(
                                    FeatureName.CREATE_UPDATE_ROI,
                                  ) || !canEditPolygon
                                }
                                id={`name-${index}`}
                                {...register(`clientPolygons.${index}.name`, {
                                  required: true,
                                })}
                              />
                            </div>
                          </div>
                          <div className="col-span-8">
                            <div className="flex w-full flex-row justify-between">
                              <div className="grid flex-1 gap-2">
                                {index === 0 && (
                                  <Label htmlFor={`zoneKey-${index}`}>
                                    Zone
                                  </Label>
                                )}

                                <div className="relative">
                                  <Input
                                    type="text"
                                    readOnly={
                                      !hasPermission(
                                        FeatureName.CREATE_UPDATE_ROI,
                                      ) || !canEditPolygon
                                    }
                                    id={`zoneKey-${index}`}
                                    placeholder="Select zone"
                                    {...register(
                                      `clientPolygons.${index}.zoneKey`,
                                    )}
                                  />
                                  <Button
                                    type="button"
                                    title="Pick from the available zones"
                                    className="text-muted-foreground hover:text-primary absolute top-0 right-0 hover:cursor-pointer"
                                    size={'icon'}
                                    variant={'link'}
                                    onClick={() => {
                                      if (
                                        !hasPermission(
                                          FeatureName.CREATE_UPDATE_ROI,
                                        )
                                      ) {
                                        return;
                                      }
                                      return openZoneSelectDialog(
                                        index,
                                        getValues(
                                          `clientPolygons.${index}.zoneKey`,
                                        ),
                                      );
                                    }}
                                  >
                                    <MapPinned className="size-5" />
                                  </Button>
                                </div>
                              </div>
                              {hasPermission(FeatureName.CREATE_UPDATE_ROI) &&
                                canEditPolygon && (
                                  <div className="ms-2 flex flex-row items-end gap-2">
                                    <Button
                                      type="button"
                                      variant={'outline'}
                                      size={'icon'}
                                      disabled={isDrawingPolygon}
                                      onClick={() => handleUpdatePolygon(index)}
                                    >
                                      <Hexagon />
                                    </Button>
                                    <Button
                                      type="button"
                                      variant={'outline'}
                                      size={'icon'}
                                      disabled={isDrawingPolygon}
                                      onClick={() => handleRemovePolygon(index)}
                                    >
                                      <X />
                                    </Button>
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div>
                        {hasPermission(FeatureName.CREATE_UPDATE_ROI) && (
                          <div className="flex flex-col gap-2">
                            {isDrawingPolygon && (
                              <>
                                <div className="text-muted-foreground text-sm">
                                  Click on the device image to create a polygon.
                                  Finish drawing by clicking on the start point
                                  or Finish.
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    type="button"
                                    variant={'outline'}
                                    size={'sm'}
                                    onClick={handleUndoDrawingPolygon}
                                  >
                                    Undo
                                  </Button>

                                  <Button
                                    type="button"
                                    variant={'outline'}
                                    disabled={drawingPolygon.length < 3}
                                    size={'sm'}
                                    onClick={handleFinishDrawing}
                                  >
                                    Finish
                                  </Button>
                                </div>
                              </>
                            )}
                            {!isDrawingPolygon && (
                              <div>
                                {showAddRoiButton ? (
                                  <Button
                                    disabled={isDrawingPolygon}
                                    variant={'outline'}
                                    size={'sm'}
                                    onClick={handleAddRoi}
                                    type="button"
                                  >
                                    <PlusIcon />
                                    Add ROI
                                  </Button>
                                ) : (
                                  <Button
                                    variant={'outline'}
                                    size={'sm'}
                                    onClick={() => setCanEditPolygon(true)}
                                    type="button"
                                  >
                                    <Edit />
                                    Edit ROIs
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
              {canEditPolygon && (
                <CardFooter className="flex justify-end gap-2">
                  <Button
                    type="submit"
                    size={'sm'}
                    disabled={!isValid || !isDirty}
                    className="w-[100px]"
                  >
                    Save
                  </Button>
                  <Button
                    className="w-[100px]"
                    variant={'outline'}
                    size={'sm'}
                    onClick={cancelChanges}
                  >
                    Cancel
                  </Button>
                </CardFooter>
              )}
            </form>
          </>
        )}
      </Card>

      {zoneSelectDialogOpen && (
        <ZonePickerDeviceDialog
          open={zoneSelectDialogOpen}
          currentZone={selectedZoneKey}
          device={device}
          onToggleOpen={handleToggleOpen}
        />
      )}
    </>
  );
};

export default PolygonEditor;
